import { useHandler } from "@redotech/react-util/hook";
import { WeightChangeSource } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import {
  OutboundParcel,
  OutboundParcelSchema,
  Parcel,
} from "@redotech/redo-model/outbound-labels/parcel";
import { Preset } from "@redotech/redo-model/outbound-labels/presets";

export const useParcelActions = ({
  fulfillmentGroups,
  updateRows,
  setSelectedPreset,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  setSelectedPreset: (preset: Preset | undefined) => void;
}): {
  onParcelChange: (
    parcel: Partial<OutboundParcel>,
    index: number,
    source?: WeightChangeSource,
  ) => void;
  onRemoveParcel: (index: number) => void;
  onParcelAdd: (parcel: Parcel) => void;
  onAllocationsFlush: (
    allocations: Map<
      number,
      { externalId: string; sku: string; quantity: number }[]
    >,
  ) => void;
} => {
  const onParcelChange = useHandler(
    (
      parcelChange: Partial<OutboundParcel>,
      index: number,
      source?: WeightChangeSource,
    ) => {
      const fulfillmentGroupUpdates: FulfillmentGroupUpdate[] =
        fulfillmentGroups.map((group) => {
          if (!group.selectedParcels?.[index]) {
            return { fulfillmentGroupId: group._id };
          }

          // Ensure nested fields are not overwritten. Customs are no longer
          // a parcel-level concern — they are stored on the group and edited
          // via dedicated customs UI flows that write top-level group fields.
          const updatedParcel = {
            ...group.selectedParcels[index],
            ...parcelChange,
            ...(parcelChange.insurance
              ? {
                  insurance: {
                    ...group.selectedParcels[index].insurance,
                    ...parcelChange.insurance,
                  },
                }
              : {}),
          } as OutboundParcel;

          // We shouldn't be able to get an update that breaks the discriminated union, but we should check and log
          try {
            OutboundParcelSchema.parse(updatedParcel);
          } catch (error) {
            console.error("Invalid parcel update", { updatedParcel, error });
            return { fulfillmentGroupId: group._id };
          }

          return {
            fulfillmentGroupId: group._id,
            source,
            selectedParcels: group.selectedParcels?.map((parcel, i) =>
              i === index ? updatedParcel : parcel,
            ),
          };
        });

      updateRows(fulfillmentGroupUpdates);
      setSelectedPreset(undefined);
    },
  );

  const onRemoveParcel = useHandler((index: number) => {
    updateRows(
      fulfillmentGroups.map((group) => ({
        fulfillmentGroupId: group._id,
        selectedParcels: group.selectedParcels?.filter((_, i) => i !== index),
      })),
    );
  });

  const onParcelAdd = useHandler((parcel: Parcel) => {
    updateRows(
      fulfillmentGroups.map((group) => ({
        fulfillmentGroupId: group._id,
        selectedParcels: [...(group.selectedParcels ?? []), parcel],
      })),
    );
  });

  const onAllocationsFlush = useHandler(
    (
      allocations: Map<
        number,
        { externalId: string; sku: string; quantity: number }[]
      >,
    ) => {
      updateRows(
        fulfillmentGroups.map((group) => ({
          fulfillmentGroupId: group._id,
          selectedParcels: (group.selectedParcels ?? []).map((parcel, i) => {
            const draft = allocations.get(i);
            if (draft === undefined) {
              return parcel;
            }
            return { ...parcel, itemAllocations: draft };
          }),
        })),
      );
    },
  );

  return { onParcelChange, onRemoveParcel, onParcelAdd, onAllocationsFlush };
};
